import { uniq } from 'lodash-es';
import { type PropsWithChildren, useMemo } from 'react';

import { useColorRule } from '../../core/color-rule/useColorRule.js';
import { ChartMessage } from '../../core/types/chart-message.js';
import { ValueRepresentation } from '../../core/types/scales.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { TimeseriesDataContext } from '../context/TimeseriesData.context.js';
import { useColorPalette } from '../hooks/useColorPalette.js';
import { useDataByYAxis } from '../hooks/useDataByYAxis.js';
import { useGapPolicy } from '../hooks/useGapPolicy.js';
import { usePointsDisplay } from '../hooks/usePointsDisplay.js';
import {
  SeriesMetadata,
  TimeseriesPerVariant,
  type TimeseriesVariantSlotProps,
  ValueRepresentationPerVariant,
  TimeseriesBandSlotProps,
  WeakMapSeries,
} from '../types/timeseries.js';
import { mergeTimeseriesFromVariants } from '../utils/timeseries.js';

type TimeseriesDataProviderProps = PropsWithChildren<{
  data: TimeseriesPerVariant;
  valueRepresentation: ValueRepresentation;
  onMessage?: (messages: ChartMessage[]) => void;
}>;

function getAllUnits(data: TimeseriesPerVariant) {
  return uniq(data.shapePriority.flatMap((v) => data[v]?.map((s) => s.unit)));
}

export function useBuildInitialMetadata(
  data: TimeseriesPerVariant,
  valueRepresentation: ValueRepresentation,
  mergedData: (TimeseriesBandSlotProps | TimeseriesVariantSlotProps)[],
) {
  const colorPalette = useColorPalette();
  const gapPolicy = useGapPolicy();
  const pointsDisplay = usePointsDisplay();
  const seriesMap: WeakMapSeries = new WeakMap();
  const allUnits = getAllUnits(data);
  const colorRules = useColorRule(mergedData, 'timeseries');

  let startingIndex = 0;

  const valueRepPerVariant: ValueRepresentationPerVariant = new Map();

  for (const variant of data.shapePriority) {
    const variantSeries = data[variant];

    for (let i = 0; i < variantSeries.length; i++) {
      const series = variantSeries[i];

      const id = crypto.randomUUID();

      const color = series.color
        ? { Default: series.color }
        : colorRules.for(series) ??
          getColorPalette(
            Array.isArray(series.name) ? series.name[0] : series.name,
            startingIndex + i,
            colorPalette,
          );

      const metadata: SeriesMetadata = {
        id,
        color,
        gapPolicy: series.gapPolicy ?? gapPolicy,
        pointsDisplay: series.pointsDisplay ?? pointsDisplay,
      };

      if (!valueRepPerVariant.get(variant) && series.valueRepresentation) {
        valueRepPerVariant.set(variant, series.valueRepresentation);
      }

      seriesMap.set(series, metadata);
    }

    if (!valueRepPerVariant.get(variant)) {
      valueRepPerVariant.set(variant, valueRepresentation);
    }

    startingIndex += variantSeries.length;
  }

  return {
    units: allUnits,
    seriesMap,
    valueRepPerVariant,
  };
}

function cloneSeries<
  T extends TimeseriesVariantSlotProps | TimeseriesBandSlotProps,
>(array: T[]) {
  return array.map((series) => ({
    ...series,
    datapoints: series.datapoints.map((datapoint) => ({ ...datapoint })),
  }));
}

export const TimeseriesDataProvider = ({
  children,
  data: _data,
  valueRepresentation,
  onMessage,
}: TimeseriesDataProviderProps) => {
  const clonedData = {
    line: cloneSeries(_data.line),
    bar: cloneSeries(_data.bar),
    area: cloneSeries(_data.area),
    band: cloneSeries(_data.band),
    shapePriority: _data.shapePriority,
  };

  const data = useDataByYAxis(clonedData);
  const mergedData = useMemo(() => mergeTimeseriesFromVariants(data), [data]);
  const { units, seriesMap, valueRepPerVariant } = useBuildInitialMetadata(
    data,
    valueRepresentation,
    mergedData,
  );

  return (
    <TimeseriesDataContext.Provider
      value={{
        data: mergedData,
        dataPerVariant: data,
        valueRepPerVariant,
        seriesMap,
        units,
      }}
    >
      {children}
    </TimeseriesDataContext.Provider>
  );
};

TimeseriesDataProvider['displayName'] = 'TimeseriesDataProvider';
